/**
 * Audio Analyzer for Lip Sync
 * Extracted and adapted from Phantasy AudioService
 */

import { SDKEventEmitter } from "../core/EventEmitter";
import { createLogger } from "../utils/logger";

type AudioDataCallback = (volume: number) => void;

const AUDIO_CONFIG = {
  fftSize: 256,
  smoothingTimeConstant: 0.8,
  minDecibels: -90,
  maxDecibels: -10,
  lipSyncSensitivity: 0.8,
  autoplayTimeout: 5000,
} as const;

export class AudioAnalyzer extends SDKEventEmitter {
  private logger: ReturnType<typeof createLogger>;
  private audioContext: AudioContext | null = null;
  private hasAttemptedAutoplay = false;

  constructor() {
    super();
    this.logger = createLogger("AudioAnalyzer");
  }

  async initialize(): Promise<void> {
    try {
      this.logger.info("Initializing AudioAnalyzer...");
      await this.initializeAudioContext();
      this.setupUserGestureHandler();
      this.logger.info("AudioAnalyzer initialized successfully");
    } catch (error) {
      this.logger.warn(
        "AudioAnalyzer initialization had issues, but will continue:",
        error,
      );
      this.setupUserGestureHandler();
    }
  }

  async playWithAnalysis(
    audioBlob: Blob,
    callback?: AudioDataCallback,
  ): Promise<void> {
    try {
      this.logger.info("Playing audio with analysis");

      // Create audio element
      const audioElement = new Audio();
      const objectUrl = URL.createObjectURL(audioBlob);
      audioElement.src = objectUrl;
      audioElement.volume = 0.8;

      // Set up analysis if we have callback and context
      if (callback && this.audioContext) {
        await this.setupAudioAnalysis(audioElement, callback);
      }

      // Track autoplay attempts
      const isFirstAttempt = !this.hasAttemptedAutoplay;
      this.hasAttemptedAutoplay = true;

      try {
        // Ensure AudioContext is running
        await this.initializeAudioContext();

        // Play the audio
        this.emit("playbackStarted");
        await audioElement.play();

        if (isFirstAttempt) {
          this.logger.info("Autoplay successful");
        }
      } catch (playError) {
        this.logger.warn("Audio autoplay prevented:", playError);

        // Simulate lip sync without actual sound if callback exists
        if (callback) {
          this.simulateLipSync(callback);
        }
      }

      // Wait for audio to finish
      await new Promise<void>((resolve) => {
        const cleanup = () => {
          URL.revokeObjectURL(objectUrl);
          this.emit("playbackEnded");
          resolve();
        };

        audioElement.addEventListener("ended", cleanup);

        // Fallback timeout
        setTimeout(() => {
          if (audioElement.paused) {
            cleanup();
          }
        }, AUDIO_CONFIG.autoplayTimeout);
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error("Audio playback failed:", err);
      this.emit("error", err);
      throw err;
    }
  }

  stop(): void {
    this.logger.debug("Audio stop requested");
    // Note: In a full implementation, we'd track current audio elements and stop them
  }

  destroy(): void {
    try {
      this.stop();

      if (this.audioContext && this.audioContext.state !== "closed") {
        this.audioContext.close();
        this.audioContext = null;
      }

      this.removeAllListeners();
      this.logger.info("AudioAnalyzer destroyed");
    } catch (error) {
      this.logger.error("Error during AudioAnalyzer destruction:", error);
    }
  }

  // Private methods

  private async initializeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error("AudioContext not available in this browser");
      }

      this.audioContext = new AudioContextClass();
      this.logger.info("AudioContext created successfully");
    }

    // Log current state for debugging
    this.logger.info(`AudioContext state: ${this.audioContext.state}`);

    // Handle suspended state (Chrome's autoplay policy)
    if (this.audioContext.state === "suspended") {
      this.logger.info("AudioContext is suspended - attempting to resume...");

      try {
        await Promise.race([
          this.audioContext.resume(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('AudioContext resume timeout')), 100)
          )
        ]);
        this.logger.info("AudioContext resumed successfully");
      } catch (error) {
        this.logger.warn(
          "AudioContext suspended - will resume after user gesture"
        );
      }
    }
  }

  private setupUserGestureHandler(): void {
    const handleUserGesture = async () => {
      if (this.audioContext && this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
          this.logger.info("AudioContext resumed after user gesture");

          // Remove listeners after successful resume
          document.removeEventListener("click", handleUserGesture);
          document.removeEventListener("keydown", handleUserGesture);
          document.removeEventListener("touchstart", handleUserGesture);
        } catch (error) {
          this.logger.error(
            "Failed to resume AudioContext on user gesture:",
            error,
          );
        }
      }
    };

    // Listen for user gestures
    document.addEventListener("click", handleUserGesture, { once: false });
    document.addEventListener("keydown", handleUserGesture, { once: false });
    document.addEventListener("touchstart", handleUserGesture, { once: false });
  }

  private async setupAudioAnalysis(
    audioElement: HTMLAudioElement,
    callback: AudioDataCallback,
  ): Promise<void> {
    if (!this.audioContext) return;

    try {
      const source = this.audioContext.createMediaElementSource(audioElement);
      const analyser = this.audioContext.createAnalyser();

      // Configure analyser for lip sync
      analyser.fftSize = AUDIO_CONFIG.fftSize;
      analyser.smoothingTimeConstant = AUDIO_CONFIG.smoothingTimeConstant;
      analyser.minDecibels = AUDIO_CONFIG.minDecibels;
      analyser.maxDecibels = AUDIO_CONFIG.maxDecibels;

      // Connect audio graph
      source.connect(analyser);
      analyser.connect(this.audioContext.destination);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLipSync = () => {
        analyser.getByteFrequencyData(dataArray);

        // Calculate volume for lip sync
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const average =
          (sum / dataArray.length / 255.0) * AUDIO_CONFIG.lipSyncSensitivity;
        const finalVolume = Math.min(average, 1.0);

        // Emit volume data event
        this.emit("volumeData", finalVolume);

        // Call callback
        callback(finalVolume);

        // Continue animation
        if (!audioElement.paused) {
          requestAnimationFrame(updateLipSync);
        } else {
          callback(0); // Reset mouth
        }
      };

      // Start analysis when audio plays
      audioElement.addEventListener("play", () => {
        this.logger.debug("Audio started playing, beginning analysis");
        updateLipSync();
      });
    } catch (error) {
      this.logger.error("Error setting up audio analysis:", error);
      this.emit(
        "error",
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }

  private simulateLipSync(callback: AudioDataCallback): void {
    this.logger.info("Simulating lip sync without audio");

    const startTime = Date.now();
    const duration = 4000; // 4 seconds

    const simulate = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= duration) {
        callback(0); // Reset mouth
        return;
      }

      // Create natural looking mouth movement
      const time = elapsed / 1000;
      const frequency = 2; // Hz
      const value = (Math.sin(time * frequency * Math.PI) + 1) / 4; // 0-0.5 range

      this.emit("volumeData", value);
      callback(value);
      requestAnimationFrame(simulate);
    };

    simulate();
  }
}
